import React, { useState } from 'react';
import axios from 'axios';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Store } from 'lucide-react';

const CATEGORIES = [
  { value: 'campaign', key: 'marketplace_category_campaign' },
  { value: 'one-shot', key: 'marketplace_category_oneshot' },
  { value: 'module', key: 'marketplace_category_module' },
  { value: 'adventure', key: 'marketplace_category_adventure' },
];

const DIFFICULTIES = [
  { value: 'beginner', key: 'marketplace_difficulty_beginner' },
  { value: 'intermediate', key: 'marketplace_difficulty_intermediate' },
  { value: 'advanced', key: 'marketplace_difficulty_advanced' },
  { value: 'expert', key: 'marketplace_difficulty_expert' },
];

const CONTENT_LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'pl', label: 'Polski' },
  { value: 'ru', label: 'Русский' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh', label: '中文' },
];

export function PublishTemplateDialog({ campaignId, campaignName, universe, open, onOpenChange, language }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: campaignName || '',
    short_description: '',
    long_description: '',
    category: 'campaign',
    difficulty: 'intermediate',
    universe: universe || '',
    game_system: '',
    tags: '',
    cover_image_url: '',
    language: language || 'fr',
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t('error_required', language));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        source_campaign_id: campaignId,
        name: form.name,
        short_description: form.short_description,
        long_description: form.long_description,
        category: form.category,
        difficulty: form.difficulty,
        universe: form.universe,
        game_system: form.game_system,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        cover_image_url: form.cover_image_url,
        language: form.language,
      };

      await axios.post('/api/marketplace/templates', payload);
      toast.success(t('marketplace_publish_success', language));
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error publishing template');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when dialog opens with new campaign
  React.useEffect(() => {
    if (open) {
      setForm(prev => ({
        ...prev,
        name: campaignName || prev.name,
        universe: universe || prev.universe,
      }));
    }
  }, [open, campaignName, universe]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <Store className="h-5 w-5" />
              {t('marketplace_publish_title', language)}
            </DialogTitle>
            <DialogDescription>
              {t('marketplace_publish_desc', language)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="tmpl-name">{t('marketplace_name', language)} *</Label>
              <Input
                id="tmpl-name"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                required
              />
            </div>

            {/* Short Description */}
            <div className="space-y-2">
              <Label htmlFor="tmpl-short">{t('marketplace_short_desc', language)}</Label>
              <Input
                id="tmpl-short"
                value={form.short_description}
                onChange={e => updateField('short_description', e.target.value)}
                maxLength={200}
                placeholder={language === 'fr' ? 'Max 200 caractères' : 'Max 200 characters'}
              />
            </div>

            {/* Long Description */}
            <div className="space-y-2">
              <Label htmlFor="tmpl-long">{t('marketplace_long_desc', language)}</Label>
              <Textarea
                id="tmpl-long"
                value={form.long_description}
                onChange={e => updateField('long_description', e.target.value)}
                rows={4}
              />
            </div>

            {/* Category & Difficulty */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('marketplace_category', language)}</Label>
                <Select value={form.category} onValueChange={v => updateField('category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{t(c.key, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('marketplace_difficulty', language)}</Label>
                <Select value={form.difficulty} onValueChange={v => updateField('difficulty', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{t(d.key, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Universe & System */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tmpl-universe">{t('marketplace_universe', language)}</Label>
                <Input
                  id="tmpl-universe"
                  value={form.universe}
                  onChange={e => updateField('universe', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tmpl-system">{t('marketplace_game_system', language)}</Label>
                <Input
                  id="tmpl-system"
                  value={form.game_system}
                  onChange={e => updateField('game_system', e.target.value)}
                />
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tmpl-tags">{t('marketplace_tags', language)}</Label>
              <Input
                id="tmpl-tags"
                value={form.tags}
                onChange={e => updateField('tags', e.target.value)}
                placeholder={language === 'fr' ? 'fantasy, médiéval, enquête' : 'fantasy, medieval, mystery'}
              />
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label htmlFor="tmpl-cover">{t('marketplace_cover_url', language)}</Label>
              <Input
                id="tmpl-cover"
                value={form.cover_image_url}
                onChange={e => updateField('cover_image_url', e.target.value)}
                placeholder="https://..."
              />
            </div>

            {/* Language */}
            <div className="space-y-2">
              <Label>{t('marketplace_language', language)}</Label>
              <Select value={form.language} onValueChange={v => updateField('language', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_LANGUAGES.map(l => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel', language)}
            </Button>
            <Button type="submit" disabled={submitting} className="fantasy-button">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {language === 'fr' ? 'Publication...' : 'Publishing...'}
                </>
              ) : (
                <>
                  <Store className="mr-2 h-4 w-4" />
                  {t('marketplace_publish', language)}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PublishTemplateDialog;
